import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cartApi } from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const LOCAL_KEY = 'elaraa_cart';
const readLocal = () => {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]'); } catch { return []; }
};
const writeLocal = (items) => localStorage.setItem(LOCAL_KEY, JSON.stringify(items));

export const CartProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState(() => readLocal());
  const [isOpen, setIsOpen] = useState(false);

  // Persist local cart for guests
  useEffect(() => {
    if (!user) writeLocal(items);
  }, [items, user]);

  // Convert server cart payload to local item shape
  const fromServer = (data) => (data.items || []).map((it) => ({
    id: it.product_id,
    name: it.product.name,
    shade: it.product.shade,
    category: it.product.category,
    price: it.product.price,
    image: it.product.image,
    quantity: it.quantity,
  }));

  // Merge local guest cart into server cart on login, then load server cart
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    const local = readLocal();
    (async () => {
      try {
        for (const li of local) {
          await cartApi.add(li.id, li.quantity);
        }
        if (local.length) writeLocal([]);
        const data = await cartApi.get();
        setItems(fromServer(data));
      } catch (e) {
        console.error('Cart sync failed', e);
      }
    })();
  }, [user, authLoading]);

  const addItem = useCallback(async (product, quantity = 1) => {
    if (user) {
      try {
        const data = await cartApi.add(product.id, quantity);
        setItems(fromServer(data));
        setIsOpen(true);
        return;
      } catch (e) { console.error(e); }
    }
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) return prev.map((i) => i.id === product.id ? { ...i, quantity: i.quantity + quantity } : i);
      return [...prev, {
        id: product.id, name: product.name, shade: product.shade,
        category: product.category, price: product.price, image: product.image,
        quantity,
      }];
    });
    setIsOpen(true);
  }, [user]);

  const updateQuantity = useCallback(async (id, quantity) => {
    if (quantity <= 0) {
      if (user) {
        const data = await cartApi.remove(id);
        setItems(fromServer(data));
      } else {
        setItems((prev) => prev.filter((i) => i.id !== id));
      }
      return;
    }
    if (user) {
      const data = await cartApi.update(id, quantity);
      setItems(fromServer(data));
    } else {
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, quantity } : i));
    }
  }, [user]);

  const removeItem = useCallback(async (id) => {
    if (user) {
      const data = await cartApi.remove(id);
      setItems(fromServer(data));
    } else {
      setItems((prev) => prev.filter((i) => i.id !== id));
    }
  }, [user]);

  const clearCart = useCallback(async () => {
    if (user) {
      const data = await cartApi.clear();
      setItems(fromServer(data));
    } else {
      setItems([]);
    }
  }, [user]);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.quantity * i.price, 0);
    return { count, subtotal };
  }, [items]);

  return (
    <CartContext.Provider value={{
      items, isOpen, setIsOpen, addItem, updateQuantity, removeItem, clearCart, ...totals,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
